/**
 * components/team/Gated.js
 *
 * The one way a vendor view asks "may this login do that?". Every gated
 * control in the dashboard goes through here so the answer, the disabled
 * look and the explanation read the same everywhere.
 *
 *   const { allowed, disabledProps } = useGate("catalogue:edit");
 *   <Button {...disabledProps} onClick={...}>Add Section</Button>
 *
 *   <Gated perm="team:manage"><Btn>Add member</Btn></Gated>   hides children
 *   <Gated anyOf={["catalogue:edit", "pricing:edit"]}>...</Gated>
 *
 * `disabledProps` is `{}` when allowed and `{ disabled: true, title }` when
 * not, so spreading it after a control's own props wins. Owners and platform
 * staff are never gated: useVendorAccess().can answers true for them, and it
 * answers true while the access set is still loading (fail open, server
 * enforces).
 */
import { useVendorAccess } from "../../hooks/useVendorAccess.js";
import { HQ_ONLY_KEYS, PRODUCT_LEVEL_KEYS, targetKeyOf } from "../../utilities/vendorScope.js";

export const GATE_TITLE = "Your role does not allow this";

/**
 * Why a branch member finds a control locked (4 Oct 2026). Product fields
 * point them at the Branches tab, where their branch's availability and price
 * live; every other head office control says whose it is.
 */
export const BRANCH_PRODUCT_TITLE =
  "Head office manages this. Use the Branches tab for this branch's availability and price.";
export const BRANCH_HQ_TITLE = "Only head office can do this.";
export const BRANCH_OUT_TITLE = "Your access does not cover this branch.";

const PRODUCT_KEYS = ["catalogue:edit", ...PRODUCT_LEVEL_KEYS];

const decide = (can, canAny, perm, anyOf) => {
  if (Array.isArray(anyOf) && anyOf.length) return canAny(anyOf);
  if (perm) return can(perm);
  return true;
};

/** The explanation for a refused key: the branch reason when that is the reason. */
export const gateTitleFor = (keys, isBranchScoped) => {
  const list = (Array.isArray(keys) ? keys : [keys]).filter(Boolean);
  if (isBranchScoped && list.length && list.every((k) => PRODUCT_KEYS.includes(k))) return BRANCH_PRODUCT_TITLE;
  if (isBranchScoped && list.length && list.every((k) => HQ_ONLY_KEYS.includes(k) || PRODUCT_KEYS.includes(k))) {
    return BRANCH_HQ_TITLE;
  }
  return GATE_TITLE;
};

export const useGate = (perm, anyOf) => {
  const { can, canAny, loaded, loading, isBranchScoped } = useVendorAccess();
  const allowed = decide(can, canAny, perm, anyOf);
  const title = allowed ? undefined : gateTitleFor(Array.isArray(anyOf) && anyOf.length ? anyOf : perm, isBranchScoped);
  return {
    allowed,
    loaded,
    loading,
    disabledProps: allowed ? {} : { disabled: true, title },
    // For controls that are not <button>s (a clickable div, a Switch wrapper).
    title,
  };
};

/**
 * A control that is head office's whatever the member's role says (4 Oct
 * 2026): what an outlet sells, creating, renaming or deleting an outlet, and
 * the vendor's own hours. A member limited to some branches finds it locked
 * with BRANCH_HQ_TITLE; everyone else gets useGate(perm) unchanged.
 */
export const useHeadOfficeGate = (perm) => {
  const gate = useGate(perm);
  const { isBranchScoped } = useVendorAccess();
  if (!gate.allowed || !isBranchScoped) return gate;
  return { ...gate, allowed: false, disabledProps: { disabled: true, title: BRANCH_HQ_TITLE }, title: BRANCH_HQ_TITLE };
};

/**
 * Why this login may not use `perm` at `location`, or undefined when it may.
 * Pure over the useVendorAccess() shape ({ canAt, isBranchScoped }), so a
 * screen that already holds the access answers the same as useLocationGate.
 * The reason names the real cause (4 Oct 2026):
 *
 *   head office login without the key        the role
 *   branch member, a head office key          head office only
 *   branch member, no single branch named     head office only
 *   branch member, a branch outside access    the branch
 *   branch member, their branch, no key       the role
 */
export const locationRefusal = ({ canAt, isBranchScoped } = {}, perm, location) => {
  if (typeof canAt !== "function" || canAt(location, perm)) return undefined;
  if (!isBranchScoped) return GATE_TITLE;
  if (HQ_ONLY_KEYS.includes(perm)) return BRANCH_HQ_TITLE;
  // "all" or no location: a change everywhere, which is head office's.
  if (targetKeyOf(location) === null) return BRANCH_HQ_TITLE;
  if (!canAt(location)) return BRANCH_OUT_TITLE;
  return GATE_TITLE;
};

/**
 * A control on one location (an outlet's switch, its hours, its contact
 * details). Returns `at(location)` -> { allowed, title }: the key held at
 * that location, so a branch member can run their own branch and no other.
 */
export const useLocationGate = (perm) => {
  const access = useVendorAccess();
  return (location) => {
    const title = locationRefusal(access, perm, location);
    return { allowed: !title, title };
  };
};

/**
 * The product forms split one submit across three keys: pricing fields are
 * pricing:edit, the enabled switch is catalogue:activate, everything else is
 * catalogue:edit. A PRICING member opens the form in pricing-only mode (only
 * the pricing inputs enabled, submit enabled); a login with none of the keys
 * gets a read-only form. The server's field gate checks what actually changed. `busy` is the form's own
 * saving / uploading flag so call sites keep a single `disabled` expression.
 *
 * A member limited to some branches gets the read-only form too (4 Oct 2026):
 * the product, its price and its on/off switch are the same at every branch,
 * so they are head office's. Their branch's availability and price are on the
 * menu's Branches tab. This only applies once /me has answered (useVendorAccess
 * fails open while loading), so the form never flashes locked for head office.
 */
export const useProductFormGate = (busy = false) => {
  const { isBranchScoped } = useVendorAccess();
  const catalogue = useGate("catalogue:edit");
  const pricing = useGate("pricing:edit");
  const activate = useGate("catalogue:activate");
  const canCatalogue = catalogue.allowed;
  const canPricing = pricing.allowed;
  const canActivate = activate.allowed;
  const any = canCatalogue || canPricing || canActivate;
  const submitTitle = any ? undefined : gateTitleFor(PRODUCT_KEYS, isBranchScoped);
  return {
    canCatalogue,
    canPricing,
    canActivate,
    pricingOnly: canPricing && !canCatalogue,
    readOnly: !any,
    branchLocked: Boolean(isBranchScoped),
    // The read-only notice the forms show above their fields.
    readOnlyMessage: isBranchScoped
      ? BRANCH_PRODUCT_TITLE
      : "Your role does not allow editing this product. The form is read-only.",
    lockCatalogue: busy || !canCatalogue,
    lockPricing: busy || !canPricing,
    lockActivation: busy || !canActivate,
    lockSubmit: busy || !any,
    catalogueTitle: catalogue.title,
    pricingTitle: pricing.title,
    activationTitle: activate.title,
    submitTitle,
  };
};

/** Renders children only when the login holds `perm` (or any of `anyOf`). */
export const Gated = ({ perm, anyOf, fallback = null, children }) => {
  const { allowed } = useGate(perm, anyOf);
  if (!allowed) return fallback;
  return children === undefined ? null : children;
};

export default Gated;
